// ============================================================
// Foretaksdokumenter (Innstillinger -> Dokumenter)
// ============================================================
// Egne dokumenter foretaket laster opp en gang — avtaler, forsikringsbevis,
// vedtekter — og som kan folge automatisk med salgsdokumenter til kunde.
//
// Fila eier bare den databasefrie delen: normalisering av metadata og hvilke
// dokumenter som gjelder for hvilken dokumenttype. Opplasting, sletting og
// lagring ligger i `src/services/backend.jsx`, og selve e-postvedlegget lastes
// av `functions/mail/companyDocuments.ts`. Utvalgsreglene ma vaere like begge
// steder, og de er derfor testet hver for seg.
const PlummeCompanyDocuments = (() => {
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const text = (value) => String(value ?? '').trim();

  // Rekkefolgen styrer avkrysningsboksene i dokumentmodalen.
  const TARGETS = ['offer', 'orderConfirmation', 'invoice', 'creditNote', 'subscription'];

  // Dokumenttypene er ren merking for foretaket selv — de styrer ingen
  // regnskapsbehandling, og skal derfor ikke brukes til logikk.
  const DOCUMENT_TYPES = [
    'agreement',
    'insurance',
    'articles',
    'authorization',
    'certificate',
    'standard_attachment',
    'other',
  ];

  // Dokumenttypen i salgsflyten («faktura», «tilbud», ...) er UI-navn, mens
  // backend-fakturaen bruker `kind`. Begge maa treffe samme flagg.
  const SALE_DOC_TYPE_TARGETS = {
    faktura: 'invoice',
    invoice: 'invoice',
    tilbud: 'offer',
    offer: 'offer',
    ordre: 'orderConfirmation',
    order_confirmation: 'orderConfirmation',
    kreditnota: 'creditNote',
    credit: 'creditNote',
    credit_note: 'creditNote',
    abonnement: 'subscription',
    subscription: 'subscription',
    list: 'subscription',
  };

  const targetForSaleDocType = (docType) => SALE_DOC_TYPE_TARGETS[text(docType).toLowerCase()] || '';

  const normalizeTargets = (raw = {}) => TARGETS.reduce((targets, key) => {
    targets[key] = raw?.[key] === true;
    return targets;
  }, {});

  const normalizeDocument = (raw) => {
    if (!raw || typeof raw !== 'object') return null;
    const id = text(raw.id);
    if (!id) return null;
    const storagePath = text(raw.storagePath);
    if (!storagePath) return null;
    return {
      id,
      name: text(raw.name) || 'Dokument',
      documentType: DOCUMENT_TYPES.includes(text(raw.documentType)) ? text(raw.documentType) : 'other',
      fileName: text(raw.fileName) || text(raw.name) || 'dokument',
      mimeType: text(raw.mimeType),
      size: Number.isFinite(Number(raw.size)) ? Number(raw.size) : 0,
      storagePath,
      downloadUrl: text(raw.downloadUrl),
      uploadedAt: text(raw.uploadedAt),
      updatedAt: text(raw.updatedAt) || text(raw.uploadedAt),
      targets: normalizeTargets(raw.targets),
    };
  };

  const normalizeDocuments = (list) => safeArray(list).map(normalizeDocument).filter(Boolean);

  const selectForTarget = (documents, target) => {
    if (!TARGETS.includes(text(target))) return [];
    return normalizeDocuments(documents).filter((document) => document.targets[target] === true);
  };

  const selectForSaleDocType = (documents, docType) => selectForTarget(documents, targetForSaleDocType(docType));

  // Vedlegget peker til fila som allerede ligger i Storage. Editoren skal ikke
  // laste den opp paa nytt, og `storagePath` er derfor det som ma folge med.
  const toAttachment = (document) => {
    const normalized = normalizeDocument(document);
    if (!normalized) return null;
    return {
      id: `company-doc-${normalized.id}`,
      companyDocumentId: normalized.id,
      name: normalized.fileName || normalized.name,
      title: normalized.name,
      type: normalized.mimeType,
      mimeType: normalized.mimeType,
      storagePath: normalized.storagePath,
      downloadUrl: normalized.downloadUrl,
      url: normalized.downloadUrl,
      source: 'company-document',
      isDefault: true,
      isImage: normalized.mimeType.startsWith('image/'),
      isPdf: normalized.mimeType === 'application/pdf',
    };
  };

  const toAttachments = (documents) => safeArray(documents).map(toAttachment).filter(Boolean);

  return {
    TARGETS,
    DOCUMENT_TYPES,
    targetForSaleDocType,
    normalizeTargets,
    normalizeDocument,
    normalizeDocuments,
    selectForTarget,
    selectForSaleDocType,
    toAttachment,
    toAttachments,
  };
})();

globalThis.PlummeCompanyDocuments = PlummeCompanyDocuments;
